import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import type {
  CoordinatorJobConfigResponse,
  CoordinatorJobConfigNewResponse,
  CoordinatorJobConfigCreatePayload,
  CoordinatorJobConfigPatchPayload,
} from './types';

// ── Query keys ─────────────────────────────────────────────────────────────

export const coordinatorJobConfigKeys = {
  all: ['coordinator-job-config'] as const,
  job: (jobId: string) => [...coordinatorJobConfigKeys.all, jobId] as const,
  jobNew: (jobId: string) =>
    [...coordinatorJobConfigKeys.all, jobId, 'new'] as const,
};

// ── GET ats_jobs/:jobId/coordinator_job_config ─────────────────────────────
// Returns null when config does not exist (err_coordinator_job_config_not_found).

export function getJobConfig(
  jobId: string
): Promise<CoordinatorJobConfigResponse['data'] | null> {
  return http
    .get(`/ats_jobs/${jobId}/coordinator_job_config`)
    .then((res: unknown) => {
      const body = res as CoordinatorJobConfigResponse;
      if (
        body?.errors?.includes?.('err_coordinator_job_config_not_found') ||
        !body?.data
      ) {
        return null;
      }
      return body.data;
    })
    .catch(() => null);
}

export const useCoordinatorJobConfig = (jobId: string | null, enabled = true) =>
  useQuery({
    queryKey: coordinatorJobConfigKeys.job(jobId!),
    queryFn: () => getJobConfig(jobId!),
    enabled: enabled && !!jobId,
    staleTime: 0,
  });

// ── GET ats_jobs/:jobId/coordinator_job_config/new (defaults for first-time) ─

export function getCoordinatorJobConfigNew(
  jobId: string
): Promise<CoordinatorJobConfigNewResponse['data'] | null> {
  return http
    .get(`/ats_jobs/${jobId}/coordinator_job_config/new`)
    .then((res: unknown) => {
      const body = res as CoordinatorJobConfigNewResponse;
      return body?.data ?? null;
    })
    .catch(() => null);
}

export const useCoordinatorJobConfigNew = (
  jobId: string | null,
  enabled: boolean
) =>
  useQuery({
    queryKey: coordinatorJobConfigKeys.jobNew(jobId!),
    queryFn: () => getCoordinatorJobConfigNew(jobId!),
    enabled: enabled && !!jobId,
    staleTime: 0,
  });

// ── POST ats_jobs/:jobId/coordinator_job_config (create) ────────────────────

export function createJobConfig(
  jobId: string,
  payload: CoordinatorJobConfigCreatePayload['coordinator_job_config']
) {
  return http
    .post(`/ats_jobs/${jobId}/coordinator_job_config`, {
      coordinator_job_config: payload,
    })
    .then(
      (res: unknown) => (res as CoordinatorJobConfigResponse)?.data ?? null
    );
}

// ── PATCH ats_jobs/:jobId/coordinator_job_config (update) ───────────────────

export function updateJobConfig(
  jobId: string,
  payload: CoordinatorJobConfigPatchPayload['coordinator_job_config']
) {
  return http
    .patch(`/ats_jobs/${jobId}/coordinator_job_config`, {
      coordinator_job_config: payload,
    })
    .then(
      (res: unknown) => (res as CoordinatorJobConfigResponse)?.data ?? null
    );
}

// ── Mutations: create or update (upsert) ───────────────────────────────────

export const useCreateCoordinatorJobConfig = (jobId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: CoordinatorJobConfigCreatePayload['coordinator_job_config']
    ) => createJobConfig(jobId!, payload),
    onSuccess: () => {
      if (jobId) {
        queryClient.invalidateQueries({
          queryKey: coordinatorJobConfigKeys.job(jobId),
        });
      }
    },
  });
};

export const useUpdateCoordinatorJobConfig = (jobId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      payload: CoordinatorJobConfigPatchPayload['coordinator_job_config']
    ) => updateJobConfig(jobId!, payload),
    onSuccess: () => {
      if (jobId) {
        queryClient.invalidateQueries({
          queryKey: coordinatorJobConfigKeys.job(jobId),
        });
      }
    },
  });
};
